/**
 * Quant readings for the chart page. Pure functions: candles in, numbers out.
 * Returns are log returns of closes. Everything uses the last `window` candles.
 */

const YEAR_SECONDS = 365.25 * 24 * 3600;

const mean = (xs) => (xs.length ? xs.reduce((a, b) => a + b, 0) / xs.length : 0);
const stdev = (xs, m = mean(xs)) =>
  xs.length > 1 ? Math.sqrt(xs.reduce((a, b) => a + (b - m) * (b - m), 0) / (xs.length - 1)) : 0;

export function logReturns(closes) {
  const out = [];
  for (let i = 1; i < closes.length; i += 1) {
    if (closes[i] > 0 && closes[i - 1] > 0) out.push(Math.log(closes[i] / closes[i - 1]));
  }
  return out;
}

/**
 * Bars per year, measured from the data itself (bars / time covered), so sessions, nights and
 * weekends are counted correctly for Nifty, FX or crypto alike. Falls back to the bar length.
 */
export function barsPerYear(candles, barSeconds) {
  if (candles.length >= 3) {
    const span = candles[candles.length - 1].time - candles[0].time;
    if (span >= 2 * 86400) return (candles.length - 1) / (span / YEAR_SECONDS);
  }
  return barSeconds > 0 ? YEAR_SECONDS / barSeconds : 252;
}

export function rsi(closes, period = 14) {
  if (closes.length <= period) return null;
  let gain = 0;
  let loss = 0;
  for (let i = 1; i <= period; i += 1) {
    const d = closes[i] - closes[i - 1];
    gain += Math.max(d, 0);
    loss += Math.max(-d, 0);
  }
  gain /= period;
  loss /= period;
  for (let i = period + 1; i < closes.length; i += 1) {
    const d = closes[i] - closes[i - 1];
    gain = (gain * (period - 1) + Math.max(d, 0)) / period;
    loss = (loss * (period - 1) + Math.max(-d, 0)) / period;
  }
  return loss === 0 ? 100 : 100 - 100 / (1 + gain / loss);
}

/** Pearson correlation of two equal-length series. */
export function correlation(a, b) {
  const n = Math.min(a.length, b.length);
  if (n < 3) return null;
  const x = a.slice(-n);
  const y = b.slice(-n);
  const mx = mean(x);
  const my = mean(y);
  let cov = 0;
  let vx = 0;
  let vy = 0;
  for (let i = 0; i < n; i += 1) {
    cov += (x[i] - mx) * (y[i] - my);
    vx += (x[i] - mx) ** 2;
    vy += (y[i] - my) ** 2;
  }
  return vx && vy ? cov / Math.sqrt(vx * vy) : null;
}

/** Returns of two candle series matched on the same timestamps. */
export function alignedReturns(candlesA, candlesB) {
  const byTime = new Map(candlesB.map((c) => [c.time, c.close]));
  const a = [];
  const b = [];
  let prevA = null;
  let prevB = null;
  candlesA.forEach((c) => {
    const other = byTime.get(c.time);
    if (other === undefined) return;
    if (prevA !== null && prevA > 0 && prevB > 0 && c.close > 0 && other > 0) {
      a.push(Math.log(c.close / prevA));
      b.push(Math.log(other / prevB));
    }
    prevA = c.close;
    prevB = other;
  });
  return [a, b];
}

/** Fewer returns than this give numbers too noisy to show (e.g. a Sharpe of +300 from 10 bars). */
export const MIN_RETURNS = 30;

export function computeQuant(candles, { window = 200, barSeconds = 0, bins = 24 } = {}) {
  const clean = (candles || []).filter((c) => c && Number.isFinite(c.close) && Number.isFinite(c.time));
  const view = clean.slice(-window);
  const closes = view.map((c) => c.close);
  const rets = logReturns(closes);
  if (rets.length < MIN_RETURNS) return null;

  const n = rets.length;
  const m = mean(rets);
  const sd = stdev(rets, m) || 1e-12;
  const skew = rets.reduce((a, r) => a + ((r - m) / sd) ** 3, 0) / n;
  const kurt = rets.reduce((a, r) => a + ((r - m) / sd) ** 4, 0) / n - 3;
  let ac = 0;
  for (let i = 1; i < n; i += 1) ac += (rets[i] - m) * (rets[i - 1] - m);
  const autocorr = ac / (n - 1) / (sd * sd);
  const perYear = barsPerYear(view, barSeconds);
  const annVol = sd * Math.sqrt(perYear);
  const sharpe = (m / sd) * Math.sqrt(perYear);

  const w = closes.slice(-21);
  const m20 = mean(w);
  const s20 = Math.sqrt(mean(w.map((c) => (c - m20) ** 2))) || 1e-12;
  const z20 = (closes[closes.length - 1] - m20) / s20;
  let path = 0;
  for (let i = 1; i < w.length; i += 1) path += Math.abs(w[i] - w[i - 1]);
  const efficiency = path ? Math.abs(w[w.length - 1] - w[0]) / path : 0;

  let peak = -Infinity;
  let maxDrawdown = 0;
  closes.forEach((c) => {
    peak = Math.max(peak, c);
    maxDrawdown = Math.min(maxDrawdown, c / peak - 1);
  });
  const sorted = [...rets].sort((a, b) => a - b);
  const var95 = sorted[Math.floor(sorted.length * 0.05)];
  const upShare = rets.filter((r) => r > 0).length / n;

  // histogram on a symmetric range, plus the matching normal curve (expected count per bin)
  const lim = Math.max(Math.abs(sorted[0]), Math.abs(sorted[sorted.length - 1])) || 1e-9;
  const width = (2 * lim) / bins;
  const counts = new Array(bins).fill(0);
  rets.forEach((r) => {
    counts[Math.min(bins - 1, Math.max(0, Math.floor((r + lim) / width)))] += 1;
  });
  const normal = [];
  for (let i = 0; i <= 60; i += 1) {
    const x = -lim + (i / 60) * 2 * lim;
    normal.push((Math.exp(-0.5 * ((x - m) / sd) ** 2) / (sd * Math.sqrt(2 * Math.PI))) * n * width);
  }

  const rollingVol = [];
  for (let i = 20; i <= n; i += 1) rollingVol.push(stdev(rets.slice(i - 20, i)) * Math.sqrt(perYear));

  const hi = Math.max(...view.map((c) => (Number.isFinite(c.high) ? c.high : c.close)));
  const lo = Math.min(...view.map((c) => (Number.isFinite(c.low) ? c.low : c.close)));
  const last = closes[closes.length - 1];

  return {
    bars: view.length,
    returns: rets,
    meanReturn: m,
    sdReturn: sd,
    skew,
    kurtosis: kurt,
    autocorr,
    barsPerYear: perYear,
    annVol,
    sharpe,
    z20,
    efficiency,
    regime: efficiency > 0.4 ? "Trend" : "Range",
    maxDrawdown,
    var95,
    upShare,
    rsi14: rsi(closes, 14),
    histogram: { counts, lim, width, normal },
    rollingVol,
    hi,
    lo,
    last,
    rangePosition: hi > lo ? Math.max(0, Math.min(1, (last - lo) / (hi - lo))) : 0.5,
  };
}
